"use client";

import clsx from "clsx";
import {
  FC,
  useState,
  useEffect,
  useRef,
  useMemo,
  Fragment,
  Dispatch,
  SetStateAction,
} from "react";

interface Point {
  heading?: string;
}

interface DynamicCurveProps {
  points: Point[];
  activePoint?: number;
  setActivePoint?: Dispatch<SetStateAction<number>>;
}

const Process: FC<DynamicCurveProps> = (props) => {
  const { points, activePoint, setActivePoint } = props;

  // Added extra values - Need for First and Last blue circle
  const updatedPoints = useMemo(() => {
    return [{ heading: "" }, ...points, { heading: "" }];
  }, [points]);

  const [path, setPath] = useState<string>("");
  const [pointPositions, setPointPositions] = useState<
    { x: number; y: number }[]
  >([]);
  const pathRef = useRef<SVGPathElement | null>(null);

  useEffect(() => {
    if (updatedPoints.length > 1) {
      const curvePath = createRightHalfCirclePath();
      setPath(curvePath);
    }
  }, [updatedPoints]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (pathRef.current && path) {
        const totalLength = pathRef.current.getTotalLength();
        const newPointPositions = updatedPoints.map((_, index) => {
          const pointPosition = pathRef.current?.getPointAtLength(
            (totalLength / (updatedPoints.length - 1)) * index,
          );
          return {
            x: pointPosition ? pointPosition.x : 0,
            y: pointPosition ? pointPosition.y : 0,
          };
        });
        setPointPositions(newPointPositions);
      }
    });
  }, [path, updatedPoints]);

  const createRightHalfCirclePath = (): string => {
    const radiusX = 200;
    const radiusY = 245;
    const centerX = 10;
    const centerY = 189;
    const startX = centerX;
    const startY = radiusX - centerY;
    const endX = centerX;
    const endY = centerY + radiusY;

    return `M${startX},${startY} A${radiusX},${radiusY} 0 0,1 ${endX},${endY}`;
  };

  return (
    <div className="relative flex h-[482px] w-full justify-start">
      <svg className="relative" width="100%" height="100%">
        <path
          ref={pathRef}
          d={path}
          stroke="#054ADA"
          strokeWidth="2"
          fill="none"
          strokeDasharray="8,8"
        />
        {pointPositions.map((position, index) => (
          <Fragment key={index}>
            <circle
              cx={position.x}
              cy={position.y}
              r={5}
              fill={
                index === 0 || index === updatedPoints.length - 1
                  ? "#054ADA"
                  : "#FC9B33"
              }
            />
          </Fragment>
        ))}
      </svg>

      {pointPositions.map((position, index) => (
        <div
          key={index}
          className="absolute -mt-[9px] ml-10 xl:ml-12"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
        >
          <div className="relative flex items-center">
            <p
              className={clsx(
                "cursor-pointer text-base font-normal text-black-400 lg:text-lg lg:!leading-5 xl:text-h6",
                activePoint !== undefined &&
                  activePoint === index - 1 &&
                  "text-black-900",
              )}
              onClick={() => setActivePoint && setActivePoint(index - 1)}
            >
              {updatedPoints[index].heading}
            </p>
            {activePoint !== undefined && activePoint === index - 1 && (
              <div className="absolute -left-[44px] top-1 size-5 bg-transparent xl:-left-[53px]">
                <div className="spin"></div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Process;
